import { Button, Menu, MenuButton, MenuItem, MenuList, Spinner, Stack, Text } from "@chakra-ui/react";
import { fetchFresh } from "../../../common/redux/fetchFresh";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronDown } from "react-icons/fi";
import {
  bffApi,
  ContractReportSortColumn,
  SortDirection,
  useGetFilteredIfrsReportPreviewQuery,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { ListDownloadItems, listSelection } from "../../../common/download/ListDownloadItems";
import { useAppDispatch } from "../../../common/redux/hooks";
import { useSub } from "../../../common/subscription/useSub";
import { base64Encode } from "../../../common/utils/base64";
import { IfrsReportDialog } from "./IfrsReportDialog";
import {
  exportLimitHit,
  exportRequest,
  fetchAllPages,
  pdfLimitHit,
  type ExportColumn,
} from "../../../common/download/listExport";
import { contractListFileName, exportCell, type ContractListQuery } from "./contractListExport";

export interface ContractListExport {
  count: number | undefined;
  isLoading: boolean;
  failed: boolean;
  columns: ExportColumn[];
  query: ContractListQuery;
  filters: string[];
}

export const ContractListDownloadMenu = ({
  encodedQ,
  contractList,
}: {
  encodedQ: string;
  contractList: ContractListExport;
}) => {
  const { t } = useTranslation();
  const { hasIFRSAccess } = useSub();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConfirmingIfrs, setIsConfirmingIfrs] = useState(false);
  const ifrsPreview = useGetFilteredIfrsReportPreviewQuery(
    { q: encodedQ },
    { skip: !hasIFRSAccess || (!isMenuOpen && !isConfirmingIfrs), refetchOnMountOrArgChange: true },
  );
  const buildBody = useContractListExport(contractList);
  const fileLimit = exportLimitHit(contractList.count ?? 0, contractList.columns.length);
  const pdfLimit = pdfLimitHit(contractList.count ?? 0, contractList.columns.length);
  const listNotReady = contractList.isLoading || contractList.failed || contractList.count === undefined;

  return (
    <>
      <Menu
        isLazy
        closeOnSelect={false}
        onOpen={() => setIsMenuOpen(true)}
        onClose={() => setIsMenuOpen(false)}
        placement="bottom-end"
      >
        {({ onClose }) => (
          <>
            <MenuButton as={Button} size="sm" variant="outline" rightIcon={<FiChevronDown />}>
              {t("Download")}
            </MenuButton>
            <MenuList>
              <ListDownloadItems
                label={t("Contract list")}
                endpoint="/api/contracts/list-export"
                baseFileName={contractListFileName(new Date())}
                errorMessage={String(t("The contract list could not be downloaded. Try again."))}
                buildBody={buildBody}
                isLoading={contractList.isLoading}
                isDisabled={listNotReady || contractList.count === 0 || fileLimit !== null}
                hint={
                  !listNotReady && contractList.count === 0
                    ? String(t("No contracts to download"))
                    : !listNotReady && fileLimit
                      ? fileLimit === "rows"
                        ? String(t("The list has too many contracts for one file. Narrow it with the filters."))
                        : String(t("The list is too large for one file. Show fewer columns, or narrow it with the filters."))
                      : undefined
                }
                pdfHint={
                  pdfLimit
                    ? pdfLimit === "rows"
                      ? String(t("Too many rows for a PDF. Choose Excel or CSV, or narrow the list with the filters."))
                      : String(
                          t("Too large for a PDF. Choose Excel or CSV, show fewer columns, or narrow the list with the filters."),
                        )
                    : undefined
                }
                onDone={onClose}
              />
              {hasIFRSAccess && (
                <IfrsMenuItem
                  preview={ifrsPreview}
                  onChoose={() => {
                    setIsConfirmingIfrs(true);
                    onClose();
                  }}
                />
              )}
            </MenuList>
          </>
        )}
      </Menu>
      {isConfirmingIfrs && <IfrsReportDialog encodedQ={encodedQ} onClose={() => setIsConfirmingIfrs(false)} />}
    </>
  );
};

const useContractListExport = (contractList: ContractListExport) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const authState = useLoggedInWithOrgContextState();

  return async () => {
    const { q, sortBy, sortDirection, sortByCustomField } = contractList.query;
    const contracts = await fetchAllPages(async (skip, limit) => {
      const page = await fetchFresh(
        dispatch(
          bffApi.endpoints.listContractReports.initiate(
            {
              q: base64Encode(JSON.stringify({ ...q, skip, limit })),
              sortBy: sortBy as ContractReportSortColumn | undefined,
              sortDirection: sortDirection as SortDirection | undefined,
              sortByCustomField,
            },
            { forceRefetch: true },
          ),
        ),
      );
      return page.reports;
    });
    const ctx = { t, selectedOrgId: authState.selectedOrg.id };
    const selection = listSelection(t, contracts.length, contractList.filters);
    return exportRequest(contractList.columns, contracts, (key, contract) => exportCell(key, contract, ctx), selection);
  };
};

const IfrsMenuItem = ({
  preview,
  onChoose,
}: {
  preview: ReturnType<typeof useGetFilteredIfrsReportPreviewQuery>;
  onChoose: () => void;
}) => {
  const { t } = useTranslation();
  const { currentData, isFetching, isError } = preview;
  const nothingToReport = currentData !== undefined && currentData.candidates.length === 0;
  return (
    <MenuItem isDisabled={isFetching || isError || !currentData || nothingToReport} onClick={onChoose}>
      <Stack spacing="0">
        <Text>
          {t("IFRS 16 report (.xlsx)")} {isFetching && <Spinner size="xs" ml="1" />}
        </Text>
        {isError && !isFetching ? (
          <Text fontSize="xs" color="smError">
            {t("Could not check the contracts. Close the menu and try again.")}
          </Text>
        ) : (
          nothingToReport && (
            <Text fontSize="xs" color="smMuted">
              {t("No contracts have filled-in IFRS values")}
            </Text>
          )
        )}
      </Stack>
    </MenuItem>
  );
};
